import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import EditableCell from '../common/EditableCell';
import ColumnFilter from '../common/ColumnFilter';
import LabourRatesExportModal from './LabourRatesExportModal';
import LabourRatesUploadModal from './LabourRatesUploadModal';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import LabourRatesAnalyticsDashboard from './LabourRatesAnalyticsDashboard';

const COLUMNS = [
  { key: 'account_number', label: 'Account #', width: 100, type: 'text' },
  { key: 'ds_trade', label: 'DS Trade', width: 120, type: 'text' },
  { key: 'ds_work_schedule', label: 'DS Work Sched', width: 120, type: 'text' },
  { key: 'ds_nb_day', label: 'DS Nb Day', width: 90, type: 'number' },
  { key: 'ds_straight', label: 'DS Straight', width: 100, type: 'number' },
  { key: 'ds_overtime', label: 'DS Overtime', width: 100, type: 'number' },
  { key: 'ds_double', label: 'DS Double', width: 100, type: 'number' },
  { key: 'ds_total_wages', label: 'DS Total Wages', width: 120, type: 'number' },
  { key: 'ds_addition', label: 'DS Addition', width: 100, type: 'number' },
  { key: 'ds_blank', label: 'DS Blank', width: 100, type: 'text' },
  { key: 'ds_notes', label: 'DS Notes', width: 150, type: 'text' },
  { key: 'column1', label: 'Column1', width: 100, type: 'text' },
  { key: 'ns_trade', label: 'NS Trade', width: 120, type: 'text' },
  { key: 'ns_work_schedule', label: 'NS Work Sched', width: 120, type: 'text' },
  { key: 'ns_nb_day', label: 'NS Nb Day', width: 90, type: 'number' },
  { key: 'ns_straight', label: 'NS Straight', width: 100, type: 'number' },
  { key: 'ns_overtime', label: 'NS Overtime', width: 100, type: 'number' },
  { key: 'ns_double', label: 'NS Double', width: 100, type: 'number' },
  { key: 'ns_total_wages', label: 'NS Total Wages', width: 120, type: 'number' },
  { key: 'ns_addition', label: 'NS Addition', width: 100, type: 'number' },
  { key: 'ns_blank', label: 'NS Blank', width: 100, type: 'text' },
  { key: 'ns_notes', label: 'NS Notes', width: 150, type: 'text' }
];

const LabourRatesLog = () => {
  const { instance, accounts } = useMsal();
  const [labourRates, setLabourRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({});

  const fetchLabourRatesRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchLabourRatesRef.current) fetchLabourRatesRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('labourRatesLog', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);
  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const fetchLabourRates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/labour-rates');
      if (response.data.success) {
        setLabourRates(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchLabourRates(); }, [fetchLabourRates]);
  useEffect(() => { fetchLabourRatesRef.current = fetchLabourRates; }, [fetchLabourRates]);

  const handleSaveCell = async (id, field, value) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/cost-breakdown/labour-rates/${id}`, { [field]: value }, syncHeaders());
      setLabourRates(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save');
    }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/labour-rates', newRow, syncHeaders());
      if (response.data.success) {
        setLabourRates(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
      }
    } catch (err) {
      alert('Failed to add: ' + err.message);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/cost-breakdown/labour-rates/${id}`, null, syncHeaders());
      setLabourRates(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleFilterChange = (columnKey, values) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: values }));
  };

  const getUniqueValues = (columnKey) => {
    const values = new Set();
    let hasBlank = false;
    labourRates.forEach(r => {
      const val = r[columnKey];
      if (val === null || val === undefined || val === '') { hasBlank = true; }
      else { values.add(String(val)); }
    });
    const sorted = Array.from(values).sort();
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  };

  const filteredAndSorted = React.useMemo(() => {
    let result = [...labourRates];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r => COLUMNS.some(c => String(r[c.key] || '').toLowerCase().includes(lower)));
    }
    Object.keys(columnFilters).forEach(key => {
      if (columnFilters[key] && columnFilters[key].length > 0) {
        result = result.filter(r => {
          const val = r[key];
          const isBlank = val === null || val === undefined || val === '';
          if (isBlank) return columnFilters[key].includes('(Blank)');
          return columnFilters[key].includes(String(val));
        });
      }
    });
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key], bVal = b[sortConfig.key];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [labourRates, searchTerm, columnFilters, sortConfig]);

  if (loading) return <div style={{ padding: '20px', color: '#fff' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: '#ff6b6b' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '20px', backgroundColor: '#1a1a2e', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          📄 Labour Rates <span style={{ backgroundColor: '#4a4a5a', padding: '2px 10px', borderRadius: '12px', fontSize: '14px' }}>{filteredAndSorted.length} records</span>
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #3a3a4a', backgroundColor: '#2a2a3a', color: '#fff', width: '200px' }} />
          <button onClick={() => setShowAnalytics(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📊 Analytics</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>➕ Add New</button>
          <button onClick={() => setShowUploadModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📥 Import</button>
          <button onClick={() => setShowExportModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📤 Export</button>
        </div>
      </div>

      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 150px)', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 3, backgroundColor: '#343a40', color: '#fff', padding: '10px 8px', minWidth: '80px', borderBottom: '2px solid #4a4a5a' }}>Actions</th>
              {COLUMNS.map(col => (
                <th key={col.key} style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#343a40', color: '#fff', padding: '10px 8px', minWidth: col.width, borderBottom: '2px solid #4a4a5a', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => handleSort(col.key)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.label} {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    <ColumnFilter values={getUniqueValues(col.key)} selectedValues={columnFilters[col.key] || []} onChange={(vals) => handleFilterChange(col.key, vals)} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isAddingNew && (
              <tr style={{ backgroundColor: '#2d4a2d' }}>
                <td style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#2d4a2d', padding: '8px' }}>
                  <button onClick={handleAddNewRow} style={{ padding: '4px 8px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '3px', marginRight: '4px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Cancel</button>
                </td>
                {COLUMNS.map(col => (
                  <td key={col.key} style={{ padding: '4px' }}>
                    <input type={col.type === 'number' ? 'number' : 'text'} value={newRow[col.key] || ''} onChange={(e) => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}
                      style={{ width: '100%', padding: '6px', backgroundColor: '#1a1a2e', color: '#fff', border: '1px solid #4a4a5a', borderRadius: '3px' }} />
                  </td>
                ))}
              </tr>
            )}
            {filteredAndSorted.map(row => (
              <tr key={row.id} style={{ backgroundColor: '#2a2a3a' }}>
                <td style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#2a2a3a', padding: '8px', borderBottom: '1px solid #3a3a4a' }}>
                  <button onClick={() => handleDeleteRow(row.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>🗑</button>
                </td>
                {COLUMNS.map(col => (
                  <td key={col.key} style={{ padding: '4px', borderBottom: '1px solid #3a3a4a' }}>
                    <EditableCell value={row[col.key]} row={row} column={col} onSave={handleSaveCell} theme="dark" accentColor="#6c757d" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showExportModal && <LabourRatesExportModal onClose={() => setShowExportModal(false)} data={labourRates} />}
      {showUploadModal && <LabourRatesUploadModal onClose={() => setShowUploadModal(false)} onUploadComplete={fetchLabourRates} />}
      
      {/* Analytics Dashboard */}
      {showAnalytics && (
        <LabourRatesAnalyticsDashboard
          data={labourRates}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default LabourRatesLog;
